import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { Popover } from "antd";

import { images } from "../../../../../assets/images/index";
import SendInbox from "../../../../../assets/customSVG/inbox/SendInbox";
import ArrowRotate from "../../../../../assets/customSVG/ArrowRotate";
import FullArrow from "../../../../../assets/customSVG/FullArrow";
import Refresh from "../../../../../assets/customSVG/Refresh";
import Xmark from "../../../../../assets/customSVG/Xmark";
import Loader from "../../../../../components/commonComponents/Loader/Index";
import { ICONS } from "../../../../../assets/icons";

import {
  getSequenceOverviewPreview,
  previewResetMessage,
  previewSendButtonClick,
  previewSendCustomTextMessage,
} from "../../../api/SquenceOverview";

import { useAspDispatch, useAspSelector } from "../../../../../test/jest-redux-hooks";

import { cn } from "../../../../../helper/cn";
import useToggle from "../../../../../hooks/useToggle";

import MediaMessage from "../preview/MediaMessage";
import Template from "../preview/Template";
import SendTextPreview from "./SendTextPreview";

const Preview = ({
  showHeader = true,
  rootClassName = "",
  bodyClassName = "",
  showClose = false,
  handleCancel,
  refreshKey = 0, // Add refreshKey prop
}) => {
  const { sequence_id, campaign_id } = useParams();
  const dispatch = useAspDispatch();
  const { currentBrand, toggleToast } = useAspSelector((s) => s.app);

  const messageContainerRef = useRef(null);

  const [previewData, setPreviewData] = useState(null);
  const [customMessage, setCustomMessage] = useState("");
  const [isSending, toggleSending] = useToggle(false);
  const [showEmojiPicker, toggleEmojiPicker] = useToggle(false);
  const [lastId, setLastId] = useState(0);

  // Move the fetch logic to a separate function
  const fetchPreviewData = useCallback(async () => {
    try {
      const payload = {
        brandId: currentBrand?.brand_id,
        campaignId: campaign_id,
      };
      const res = await getSequenceOverviewPreview(sequence_id, payload);
      if (res.status === 200) {
        setPreviewData(res.data);
        if (res.data.trigger_type === "CAMP_QR_URL") {
          setCustomMessage(res.data.url_text);
        }
        scrollToBottom();
      }
    } catch (err) {
      console.warn(err);
      setPreviewData(null);
    }
  }, [sequence_id, campaign_id, currentBrand]);

  // Fetch preview on mount or when refreshKey changes
  useEffect(() => {
    fetchPreviewData();
  }, [fetchPreviewData, refreshKey]);

  // Update lastId whenever previewData changes
  useEffect(() => {
    if (!previewData) return;
    const sent = previewData?.data?.preview?.filter((m) => m.message_type === "SENT");
    setLastId(sent?.length ? sent[sent.length - 1].id : 0);
  }, [previewData]);

  const scrollToBottom = () => {
    const el = messageContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  };

  const appendPreview = (newMsgs) => {
    setPreviewData((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        preview: [...prev.data.preview, ...newMsgs],
      },
    }));
    setTimeout(scrollToBottom, 300);
  };

  const isBroadcastAllowed = useCallback(() => {
    if (!previewData) return false;
    if (previewData.trigger_type === "CAMP_WAPP_BCAST") {
      return previewData.data.preview.length > 0;
    }
    return true;
  }, [previewData]);

  const isQrAllowed = useCallback(() => {
    if (!previewData) return false;
    if (previewData.trigger_type === "CAMP_QR_URL") {
      const count = previewData.data.preview.length;
      return count === 0 ? customMessage.length > 0 : count > 0;
    }
    return true;
  }, [previewData, customMessage]);

  const isAllowed = isBroadcastAllowed() && isQrAllowed();

  const handleButtonClick = async ({ data, button }) => {
    try {
      const query = {
        brandId: currentBrand.brand_id,
        campaignId: campaign_id,
        previewId: previewData.data.id,
      };
      let buttonText;
      let buttonId;

      if (data.type === "BUTTON") {
        buttonText = button.reply.title;
        buttonId = button.reply.id;
      } else if (data.type === "LIST") {
        buttonText = button.title;
        buttonId = button.id;
      } else if (data.type === "FLOW") {
        buttonText = button.text || button.parameters?.flow_cta || "Flow";
        buttonId = button.id || button.parameters?.flow_id || "flow";
      } else {
        buttonText = button.text;
        buttonId = button.id;
      }

      const payload = {
        preview_step_id: data.id,
        clicked: buttonId,
        button_text: buttonText,
      };
      const res = await previewSendButtonClick(sequence_id, query, payload);
      if (res.status === 200) appendPreview(res.data.data);
    } catch (err) {
      console.warn(err);
    }
  };

  const handleReset = async () => {
    if (!isAllowed) return;
    try {
      const query = {
        brandId: currentBrand.brand_id,
        campaignId: campaign_id,
        previewId: previewData.data.id,
        trigger_type: previewData.trigger_type,
      };
      const res = await previewResetMessage(sequence_id, query);
      if (res.status === 200) {
        setPreviewData((prev) => ({
          ...prev,
          data: { ...prev.data, preview: res.data.data },
        }));
        setCustomMessage(previewData.url_text || "");
      }
    } catch (err) {
      console.warn(err);
    }
  };

  const handleSendCustom = async (e) => {
    e.preventDefault();
    if (!isAllowed || isSending) return;

    toggleSending();
    try {
      const query = {
        brandId: currentBrand.brand_id,
        campaignId: campaign_id,
        previewId: previewData.data.id,
        trigger_type: previewData.trigger_type,
      };
      const payload = {
        message: customMessage.trim(),
        last_sent_preview_step_id: lastId,
      };
      const res = await previewSendCustomTextMessage(sequence_id, query, payload);
      if (res.status === 200) {
        appendPreview(res.data.data);
        setCustomMessage("");
      } else {
        dispatch({
          type: "updateToggleToast",
          payload: [
            ...toggleToast,
            {
              id: toggleToast.length + 1,
              content: res.response.data.message,
              status: "Error",
            },
          ],
        });
      }
    } catch (err) {
      console.warn(err);
    } finally {
      toggleSending();
    }
  };

  const handleEmojiSelect = (emoji) => {
    if (emoji.native) {
      setCustomMessage((msg) => msg + emoji.native);
      toggleEmojiPicker(false);
    }
  };

  return (
    <div
      className={cn(
        "w-full h-full overflow-hidden flex flex-col",
        showHeader && "bg-white rounded-2xl",
        rootClassName
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "flex items-center justify-between px-4",
          showHeader
            ? "h-[clamp(56px,10%,56px)] border-b border-[var(--border-100)]"
            : "h-16"
        )}
      >
        {showHeader ? (
          <>
            <h5 className="text-base font-semibold text-[#2D3036]">Preview</h5>
            <button
              onClick={handleReset}
              disabled={!isAllowed}
              className={cn(
                "flex items-center gap-1 py-1.5 px-2 rounded-lg text-sm font-medium",
                isAllowed
                  ? "bg-[#05805C] text-white cursor-pointer"
                  : "bg-[var(--BG-50)] text-[var(--font-600)] cursor-not-allowed"
              )}
            >
              <ArrowRotate />
              Reset
            </button>
          </>
        ) : (
          <>
            <button onClick={handleCancel} className="size-6">
              <FullArrow className="rotate-180" />
            </button>
            <div className="flex items-center gap-3 flex-1">
              {previewData?.profile_info?.profile_picture_url ? (
                <img
                  src={previewData.profile_info.profile_picture_url}
                  alt="Profile"
                  className="size-8 rounded-full"
                />
              ) : (
                <div className="size-8 bg-[var(--BG-50)] rounded-full flex items-center justify-center text-base font-semibold text-[var(--contentText)]">
                  {previewData?.profile_info?.account_name?.[0]}
                </div>
              )}
              <span className="text-base font-medium text-[#2D3036]">
                {previewData?.profile_info?.account_name}
              </span>
            </div>
            <button
              onClick={handleReset}
              disabled={!isAllowed}
              className={cn(
                "flex items-center gap-1 py-1.5 px-2 rounded-lg text-sm font-medium",
                isAllowed
                  ? "bg-[#05805C] text-white"
                  : "bg-[var(--BG-50)] text-[var(--font-600)] cursor-not-allowed"
              )}
            >
              <ArrowRotate />
              Reset
            </button>
            {showClose && (
              <button onClick={handleCancel}>
                <Xmark
                  width={24}
                  height={24}
                  strokeWidth={1.6}
                  className="p-1 rounded-full hover:bg-[#FFE3E0] hover:stroke-[#FF543E]"
                />
              </button>
            )}
          </>
        )}
      </div>

      {/* Profile Info (when showHeader) */}
      {showHeader && (
        <div className="flex items-center gap-3 px-4 py-2">
          {previewData?.profile_info?.profile_picture_url ? (
            <img
              src={previewData.profile_info.profile_picture_url}
              alt="Profile"
              className="size-8 rounded-full"
            />
          ) : (
            <div className="size-8 bg-[var(--BG-50)] rounded-full flex items-center justify-center text-base font-semibold text-[var(--contentText)]">
              {previewData?.profile_info?.account_name?.[0]}
            </div>
          )}
          <span>{previewData?.profile_info?.account_name}</span>
        </div>
      )}

      {/* Messages Container */}
      <div
        ref={messageContainerRef}
        className={cn(
          "flex-1 overflow-y-auto px-2.5 py-2.5",
          bodyClassName
        )}
        style={{ backgroundImage: `url(${images.bg})` }}
      >
        {previewData?.data?.preview?.map((msg) => (
          <div key={msg.id} className="mb-2">
            {msg.message_type === "REPLIED" ? (
              <div className="flex justify-end">
                <SendTextPreview text={msg.data.text} className="white" />
              </div>
            ) : (
              <>
                {msg.type === "TEXT" ? (
                  <SendTextPreview
                    text={msg.data.text?.body || msg.data.text}
                    type="SENT"
                  />
                ) : ["MARKETING", "UTILITY"].includes(msg?.data?.category) ? (
                  <Template
                    data={msg.data}
                    node={msg}
                    category={msg.data.category}
                    categoryType={msg.data.subcategory_type}
                    allowToShowEdge={false}
                    onButtonClick={(btn) => handleButtonClick({ data: msg, button: btn })}
                    rootClassName="w-[75%] min-w-[75%]"
                    btnClassName="w-full border-t border-[var(--border-100)] rounded-none"
                    previewType="preview"
                  />
                ) : (
                  <MediaMessage
                    previewType="preview"
                    node={msg}
                    allowToShowEdge={false}
                    rootClassName="w-[75%] rounded-2xl p-3.5"
                    onButtonClick={(btn) => handleButtonClick({ data: msg, button: btn })}
                  />
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Composer */}
      <form
        onSubmit={handleSendCustom}
        className={cn(
          "flex items-center gap-2.5 px-4 py-2 border-t border-[var(--border-100)]",
          showHeader ? "" : "rounded-t-xl"
        )}
      >
        <Popover
          trigger={["click"]}
          content={
            <Picker
              data={data}
              onEmojiSelect={handleEmojiSelect}
              theme="green"
              searchPosition="none"
              previewPosition="none"
            />
          }
        >
          <button type="button" onClick={toggleEmojiPicker}>
            <img src={ICONS.step3EmojiIcon} alt="Emoji" className="size-5" />
          </button>
        </Popover>

        <input
          type="text"
          className={cn(
            "flex-1 text-sm font-normal px-2 py-1 border rounded",
            isAllowed ? "cursor-text" : "cursor-not-allowed bg-[var(--BG-50)]"
          )}
          value={customMessage}
          onChange={(e) => isAllowed && setCustomMessage(e.target.value)}
          placeholder="Message"
          disabled={!isAllowed}
        />

        <button type="submit" disabled={!isAllowed || isSending}>
          {isSending ? (
            <Loader Width={20} Height={20} />
          ) : (
            <SendInbox
              color={isAllowed && customMessage.length > 0 ? "var(--primary)" : "var(--font-600)"}
              width={18}
              height={18}
            />
          )}
        </button>
      </form>
    </div>
  );
};

export default Preview;
